import styles from './primeiroAdquirenteListaProcessos.module.scss';
import { BrButton, BrLoading } from '@govbr-ds/react-components';

import { useContext, useEffect, useState, useCallback } from 'react';
import { DiefCfemContext } from '../../../../../contexts/DiefCfemContext';
import { IAquisicao, IAquisicaoListagem, IAquisicaoResumo } from '../../../../../interfaces/IAquisicao';
import { AquisicaoTable } from './components/aquisicaoTable/aquisicaoTable';
import { useBackEndRequests } from '../../../lib/backEndRequests';
import { useNotificacao } from '../../../../../hooks/useNotificacao';

interface IPrimeiroAdquirenteListaProcessosProps {
  setNovaAquisicao: React.Dispatch<React.SetStateAction<boolean>>;
  setEdicaoAquisicao: React.Dispatch<React.SetStateAction<IAquisicao | undefined>>;
}

function PrimeiroAdquirenteListaProcessos({
  setNovaAquisicao,
  setEdicaoAquisicao,
}: IPrimeiroAdquirenteListaProcessosProps) {
  const [salvarRascunhoText, setSalvarRascunhoText] = useState(
    <>
      <i className={`fas fa-save fa-md  ${styles.columnIcon}`} />
      <span> Salvar Rascunho</span>
    </>
  );

  const context = useContext(DiefCfemContext);
  const declaracaoPrimeiroAdquirente = context?.declaracaoPrimeiroAdquirente;
  const hasTributoCadastrado = !!declaracaoPrimeiroAdquirente?.aquisicoes?.some((aquisicao) =>
    aquisicao?.componentesBaseDeCalculo?.some((baseCalculo) =>
      baseCalculo?.codigosNCM?.some((codigoNCM) => (codigoNCM.tributosValores?.length ?? 0) > 0)
    )
  );

  const headers = [
    { field: 'substancia', label: 'Substância' },
    { field: 'municipio', label: 'Município/UF' },
    { field: 'quantidade', label: 'Quantidade' },
    { field: 'unidadeMedida', label: 'Unidade' },
    { field: 'precoUnitario', label: 'Preço Unitário' },
    ...(hasTributoCadastrado ? [{ field: 'deducoes', label: 'Deduções' }] : []),
    { field: 'valorAquisicao', label: 'Valor de Aquisição' },
    { field: 'acao', label: 'Operações' },
  ];

  const [aquisicaoListagemData, setAquisicaoListagemData] = useState<IAquisicaoListagem[]>([]);

  const mostrarNotificacao = useNotificacao();

  const { postSalvarRascunhoDeclaracao } = useBackEndRequests();

  const mapAquisicoesToListagem = useCallback(() => {
    const aquisicoes: IAquisicao[] = declaracaoPrimeiroAdquirente?.aquisicoes || [];
    const resumos = mapAquisicoesToResumo(aquisicoes);
    const listagem = mapResumoToListagem(resumos);
    setAquisicaoListagemData(listagem);
  }, [declaracaoPrimeiroAdquirente?.aquisicoes]); // Dependendo de 'aquisicoes'

  useEffect(() => {
    if (declaracaoPrimeiroAdquirente?.aquisicoes) {
      mapAquisicoesToListagem();
    }
  }, [mapAquisicoesToListagem, declaracaoPrimeiroAdquirente?.aquisicoes]);

  function mapAquisicoesToResumo(aquisicoes: IAquisicao[]): IAquisicaoResumo[] {
    const aquisicoesFuncao = aquisicoes.map((aquisicao) => ({
      aquisicao: aquisicao,
      quantidade: aquisicao.quantidadeTotal,
      precoUnitario: (parseFloat(aquisicao.valorTotal) / aquisicao.quantidadeTotal).toString(), //formatCurrency((('150.00'))),
      valorAquisicao: aquisicao.valorTotal,
    }));
    return aquisicoesFuncao;
  }

  function mapResumoToListagem(resumos: IAquisicaoResumo[]): IAquisicaoListagem[] {
    const agrupados: { [key: string]: IAquisicaoResumo[] } = {};

    resumos.forEach((resumo) => {
      if (!agrupados[resumo.aquisicao.numeroProcesso]) {
        agrupados[resumo.aquisicao.numeroProcesso] = [];
      }
      agrupados[resumo.aquisicao.numeroProcesso].push(resumo);
    });

    return Object.keys(agrupados).map((numeroProcesso) => ({
      numeroProcesso,
      titulo: 'Permissão de Lavra Garimpeira',
      aquisicoes: agrupados[numeroProcesso],
    }));
  }

  const salvarRascunho = async () => {
    setSalvarRascunhoText(
      <>
        <BrLoading />
      </>
    );

    if (declaracaoPrimeiroAdquirente)
      await postSalvarRascunhoDeclaracao(declaracaoPrimeiroAdquirente)
        .then(() => {
          setSalvarRascunhoText(
            <div className={styles.green}>
              <i className={`fas fa-check-circle fa-md  ${styles.columnIcon}`} />
              <span> Rascunho Salvo</span>
            </div>
          );
          setTimeout(() => {
            setSalvarRascunhoText(
              <>
                <i className={`fas fa-save fa-md  ${styles.columnIcon}`} />
                <span> Salvar Rascunho</span>
              </>
            );
          }, 5000);
        })
        .catch((err) =>
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao salvar rascunho',
            ...(err && { complementoSentryId: err }),
          })
        );
    else
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Erro ao salvar rascunho',
      });
  };

  return (
    <div className={styles.containerPrincipal}>
      <div className={styles.cabecalho}>
        <div className={styles.titleContainer}>
          <h4 className={styles.title}>Aquisições</h4>
        </div>
        <div className={styles.botoes}>
          <div className={styles.botao}>
            <BrButton
              secondary
              onClick={() => salvarRascunho()}
              disabled={
                !declaracaoPrimeiroAdquirente?.aquisicoes || declaracaoPrimeiroAdquirente?.aquisicoes?.length === 0
              }
            >
              {salvarRascunhoText}
            </BrButton>
          </div>
          <div className={styles.botao}>
            <BrButton
              primary
              onClick={() => {
                window.scrollTo(0, 0);
                setNovaAquisicao(true);
              }}
            >
              <i className={`fas fa-plus fa-md  ${styles.columnIcon}`} />
              <span> Nova Aquisição</span>
            </BrButton>
          </div>
        </div>
      </div>
      <AquisicaoTable
        data={aquisicaoListagemData || []}
        headers={headers}
        isLoading={false}
        mapAquisicoesToListagem={mapAquisicoesToListagem}
        setEdicaoAquisicao={setEdicaoAquisicao}
        setNovaAquisicao={setNovaAquisicao}
      />
    </div>
  );
}

export default PrimeiroAdquirenteListaProcessos;
